<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <div class="p-relative header-image pb-5"></div>

    <b-container class="custom-container mt-5">
      <b-row>
        <b-col cols="12" class="px-3">
          <open-to-work-tabs />

          <h4 class="font-weight-bold pt-4 pb-1 align-middle">
            My Applications
          </h4>
          <p class="text-muted mb-4">
            Jobs you've applied for and where each one is up to.
          </p>

          <b-row class="mb-2">
            <b-col sm="6" md="4" lg="3" class="mb-3">
              <b-form-select
                v-model="statusFilter"
                :options="statusOptions"
                data-test="status-filter"
                @change="onFilterChanged"
              />
            </b-col>
          </b-row>

          <template v-if="applications.length">
            <p v-if="total" class="application-count">
              {{ total }} application{{ total === 1 ? '' : 's' }}
            </p>

            <div
              v-for="application in applications"
              :key="application.id"
              class="application-card"
              :class="`application-card--${shownStatus(application)}`"
            >
              <div class="application-head">
                <b-avatar
                  :src="application.job.company.logo_url || undefined"
                  :text="initials(application.job.company.name)"
                  square
                  size="48px"
                  class="application-logo"
                />
                <div class="application-heading">
                  <div class="application-title">
                    {{ application.job.title }}
                  </div>
                  <div class="application-company">
                    {{ application.job.company.name }}
                  </div>
                </div>
                <span
                  class="application-status"
                  :class="`application-status--${shownStatus(application)}`"
                >
                  {{ statusText(shownStatus(application)) }}
                </span>
              </div>

              <div class="application-meta">
                <span v-if="application.job.trade" class="application-chip">
                  {{ application.job.trade.name }}
                </span>
                <span>{{ locationLabel(application.job) }}</span>
                <span
                  >&middot; Applied {{ timeAgo(application.applied_at) }}</span
                >
              </div>

              <p v-if="!application.job.is_active" class="application-closed">
                {{
                  shownStatus(application) === 'rejected' &&
                  application.status !== 'withdrawn'
                    ? 'Unsuccessful - this job has closed.'
                    : 'This job is no longer taking applications.'
                }}
              </p>

              <!-- The company shortlisted them and asked to get in touch. -->
              <contact-share-request
                v-if="application.contact_share_status === 'requested'"
                :company-name="application.job.company.name"
                :busy="sharingId === application.id"
                @share="onShareContact(application)"
              />
              <p
                v-else-if="application.contact_share_status === 'shared'"
                class="application-closed"
              >
                You shared your contact details with
                {{ application.job.company.name }}.
              </p>

              <div v-if="showActions(application)" class="application-footer">
                <b-link
                  v-if="canViewJob(application)"
                  class="application-link"
                  :to="{
                    name: 'open-to-work.job-page',
                    params: { jobId: application.job_posting_id },
                  }"
                >
                  View job
                </b-link>
                <b-button
                  v-if="canWithdraw(application)"
                  size="sm"
                  variant="outline-secondary"
                  class="withdraw-btn"
                  :disabled="withdrawingId === application.id"
                  @click="askWithdraw(application)"
                >
                  Withdraw
                </b-button>
                <!-- Withdrawn but the job is still live: the job page shows
                     the apply form again, so send them back to reapply. -->
                <b-button
                  v-else-if="
                    application.status === 'withdrawn' &&
                    application.job_viewable !== false
                  "
                  size="sm"
                  variant="outline-primary"
                  class="reapply-btn"
                  :to="{
                    name: 'open-to-work.job-page',
                    params: { jobId: application.job_posting_id },
                  }"
                >
                  Reapply
                </b-button>
              </div>
            </div>
          </template>

          <p
            v-else-if="!isLoading && statusFilter"
            class="text-muted py-5 text-center"
          >
            No applications with this status.
          </p>
          <p v-else-if="!isLoading" class="text-muted py-5 text-center">
            You haven't applied for any jobs yet.
            <b-link :to="{ name: 'open-to-work.jobs-page' }"
              >Browse jobs</b-link
            >
          </p>

          <b-pagination
            v-if="total > perPage"
            v-model="currentPage"
            :total-rows="total"
            :per-page="perPage"
            align="center"
            class="mt-3"
            @change="onPageChanged"
          />
        </b-col>
      </b-row>
    </b-container>

    <confirmation-modal
      :show-modal="showWithdrawModal"
      title="Withdraw Application"
      message="Are you sure you want to withdraw your application for this job?"
      ok-label="YES, WITHDRAW"
      @close="showWithdrawModal = false"
      @ok="onWithdraw"
    />
  </b-overlay>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import ConfirmationModal from '@/components/modals/ConfirmationModal.vue'
import ContactShareRequest from '@/components/jobPostings/ContactShareRequest.vue'
import OpenToWorkTabs from '@/components/openToWork/OpenToWorkTabs.vue'
import apiToasts from '@/mixins/apiToasts'
import {
  TRADIE_APPLICATION_STATUS_LABELS,
  canWithdrawStatus,
  locationLabel,
  timeAgo,
  tradieStatusText,
} from '@/utils/jobPostings'

export default {
  name: 'OpenToWorkApplicationsPage',

  components: { ConfirmationModal, ContactShareRequest, OpenToWorkTabs },

  mixins: [apiToasts],

  data() {
    return {
      isLoading: false,
      currentPage: 1,
      perPage: 20,
      statusFilter: '',
      withdrawingId: null,
      withdrawTarget: null,
      showWithdrawModal: false,
      sharingId: null,
    }
  },

  computed: {
    ...mapGetters('jobBoard', ['applications', 'applicationsMeta']),

    // The API orders applications by status in this same order.
    statusOptions() {
      return [
        { value: '', text: 'All statuses' },
        // Interview / Offer are the company's stages; the tradie sees them as
        // Shortlisted, and the API's shortlisted filter covers them.
        ...[
          'hired',
          'shortlisted',
          'new',
          'rejected',
          'withdrawn',
          'closed',
        ].map((value) => ({
          value,
          text: TRADIE_APPLICATION_STATUS_LABELS[value],
        })),
      ]
    },

    total() {
      const meta = this.applicationsMeta
      return meta && meta.total ? Number(meta.total) : 0
    },
  },

  async created() {
    await this.load()
  },

  methods: {
    ...mapActions('jobBoard', [
      'fetchMyApplications',
      'withdrawApplication',
      'shareContact',
    ]),

    locationLabel,
    timeAgo,
    statusText: tradieStatusText,

    // The API's display_status already folds in "job closed", so an application
    // that was never progressed reads as not successful. Fall back to the
    // stored status for anything it did not send.
    shownStatus(application) {
      return application.display_status || application.status
    },

    async onShareContact(application) {
      this.sharingId = application.id
      try {
        await this.shareContact(application.id)
        this.showToast('Success', 'Your contact details were shared.')
      } catch (err) {
        this.showToast(
          'Error',
          this.errorMessage(err, 'Could not share your contact details.'),
          'danger',
        )
      } finally {
        this.sharingId = null
      }
    },

    initials(name) {
      return (name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('')
    },

    // The API only lets you withdraw from a job that is still live, and
    // there's nothing to withdraw once it already has been or you were passed on.
    canWithdraw(application) {
      return application.job.is_active && canWithdrawStatus(application.status)
    },

    // "Not successful" applications don't link back to the job, and neither
    // does one whose job the company hid from this tradie (not suited).
    canViewJob(application) {
      return (
        this.shownStatus(application) !== 'rejected' &&
        application.job_viewable !== false
      )
    },

    // A closed job 404s on the board, so there is nothing to open or withdraw.
    showActions(application) {
      return application.job.is_active
    },

    async load() {
      this.isLoading = true
      try {
        await this.fetchMyApplications({
          page: this.currentPage,
          perPage: this.perPage,
          status: this.statusFilter,
        })
      } catch (err) {
        this.showToast('Error', this.errorMessage(err), 'danger')
      } finally {
        this.isLoading = false
      }
    },

    onFilterChanged() {
      this.currentPage = 1
      this.load()
    },

    onPageChanged(page) {
      this.currentPage = page
      this.load()
    },

    askWithdraw(application) {
      this.withdrawTarget = application
      this.showWithdrawModal = true
    },

    async onWithdraw() {
      this.showWithdrawModal = false
      const application = this.withdrawTarget
      if (!application) return
      this.withdrawingId = application.id
      try {
        await this.withdrawApplication(application.job_posting_id)
        this.showToast('Success', 'Application withdrawn.')
      } catch (err) {
        this.showToast(
          'Error',
          this.errorMessage(err, 'Could not withdraw your application.'),
          'danger',
        )
      } finally {
        this.withdrawingId = null
        this.withdrawTarget = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// Gray values follow the newer Tailwind-style scale in DESIGN.md.
.application-count {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.75rem;
}

.application-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  // A thin status-coloured edge, the same cue the company's applicant cards use.
  border-left: 4px solid #d1d5db;
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  margin-bottom: 1rem;
  // Resting card shadow from DESIGN.md.
  box-shadow: 0px 10px 25px #00429d0d;

  &--new {
    border-left-color: #006cff;
  }

  &--shortlisted,
  &--interview,
  &--offer,
  &--hired {
    border-left-color: #1e9e63;
  }

  &--withdrawn,
  &--closed {
    opacity: 0.7;
  }
}

.application-head {
  display: flex;
  align-items: flex-start;
  gap: 0.9rem;
}

.application-logo {
  flex-shrink: 0;
  border-radius: 10px;
  background-color: #e6f0ff;
  color: #006cff;
  font-weight: 700;
}

.application-heading {
  flex: 1;
  min-width: 0;
}

.application-title {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
  color: #111827;
  overflow-wrap: anywhere;
}

.application-company {
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.1rem;
}

// A plain span, not b-badge: the global .badge sizing makes the pill huge.
.application-status {
  flex-shrink: 0;
  padding: 0.25rem 0.75rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1.4;
  background-color: #f3f4f6;
  color: #6b7280;

  &--new {
    background-color: #e6f0ff;
    color: #006cff;
  }

  &--shortlisted,
  &--interview,
  &--offer,
  &--hired {
    background-color: #e3f2e9;
    color: #1e9e63;
  }

  &--withdrawn,
  &--closed {
    background-color: #f9fafb;
    color: #9ca3af;
  }
}

.application-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.6rem;
  margin-top: 0.9rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.application-chip {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  background-color: #e6f0ff;
  color: #006cff;
}

.application-closed {
  margin: 0.75rem 0 0;
  font-size: 0.875rem;
  color: #9ca3af;
}

.application-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-top: 0.9rem;
  padding-top: 0.9rem;
  border-top: 1px solid #f3f4f6;
}

.application-link {
  font-size: 0.875rem;
  font-weight: 600;
}
</style>
